/* Shreya — predictive search.
   Enhances any <predictive-search-form> (search drawer, search page) containing:
   [data-search-input], [data-search-results], optional [data-search-default], [data-search-status], [data-search-clear].
   Without this script the markup is a normal GET form to /search. */
(() => {
  if (customElements.get('predictive-search-form')) return;

  const MIN_CHARS = 2;
  const RESOURCES = 'product,collection,page,article,query';

  class PredictiveSearchForm extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('form');
      this.input = this.querySelector('[data-search-input]');
      this.results = this.querySelector('[data-search-results]');
      this.defaults = this.querySelector('[data-search-default]');
      this.status = this.querySelector('[data-search-status]');
      this.clearBtn = this.querySelector('[data-search-clear]');
      if (!this.input || !this.results) return;

      this.endpoint = this.dataset.url || (window.Theme && Theme.routes && Theme.routes.predictiveSearch) || '/search/suggest';
      this.cache = new Map();
      this.activeIndex = -1;

      const debounce = (window.Theme && Theme.debounce) || ((fn, wait) => { let t; return (...a) => { clearTimeout(t); t = setTimeout(() => fn(...a), wait); }; });
      this.onInput = debounce(() => this.query(), 260);

      this.input.addEventListener('input', () => {
        this.toggleClear();
        this.onInput();
      });
      this.input.addEventListener('keydown', (e) => this.onKeydown(e));
      this.input.addEventListener('focus', () => {
        if (this.term.length >= MIN_CHARS && this.results.innerHTML.trim()) this.showResults();
      });

      if (this.form) {
        this.form.addEventListener('reset', () => {
          setTimeout(() => {
            this.toggleClear();
            this.hideResults();
            this.input.focus();
          });
        });
        this.form.addEventListener('submit', (e) => {
          if (!this.term) { e.preventDefault(); this.input.focus(); }
        });
      }

      // Popular term pills fill the input and search inline instead of leaving the page
      this.addEventListener('click', (e) => {
        const pill = e.target.closest('[data-search-term]');
        if (!pill || e.metaKey || e.ctrlKey) return;
        e.preventDefault();
        this.input.value = pill.dataset.searchTerm;
        this.toggleClear();
        this.query();
        this.input.focus();
      });

      // Focus the field whenever the surrounding drawer opens
      this.drawer = this.closest('drawer-panel');
      if (this.drawer) {
        this.onDrawerOpen = () => setTimeout(() => this.input.focus({ preventScroll: true }), 120);
        this.drawer.addEventListener('drawer:open', this.onDrawerOpen);
      }

      this.toggleClear();
    }

    disconnectedCallback() {
      if (this.controller) this.controller.abort();
      if (this.drawer && this.onDrawerOpen) this.drawer.removeEventListener('drawer:open', this.onDrawerOpen);
    }

    get term() { return this.input.value.trim(); }

    toggleClear() {
      if (this.clearBtn) this.clearBtn.hidden = this.input.value.length === 0;
    }

    async query() {
      const term = this.term;
      if (term.length < MIN_CHARS) {
        this.hideResults();
        return;
      }
      const key = term.toLowerCase();
      if (this.cache.has(key)) {
        this.render(this.cache.get(key));
        return;
      }

      if (this.controller) this.controller.abort();
      this.controller = new AbortController();
      this.classList.add('is-loading');
      this.announce(this.dataset.loadingText || '');

      const params = new URLSearchParams({
        q: term,
        'resources[type]': RESOURCES,
        'resources[limit]': '6',
        'resources[limit_scope]': 'each',
        'resources[options][prefix]': 'last',
        section_id: 'predictive-search',
      });

      try {
        const res = await fetch(`${this.endpoint}?${params.toString()}`, { signal: this.controller.signal });
        if (!res.ok) throw new Error(res.status);
        const html = await res.text();
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const fresh = doc.querySelector('#predictive-search-results');
        const markup = fresh ? fresh.innerHTML : '';
        this.cache.set(key, markup);
        // Only paint if the visitor hasn't typed something else meanwhile
        if (this.term.toLowerCase() === key) this.render(markup);
      } catch (err) {
        if (err.name !== 'AbortError') this.hideResults();
      } finally {
        this.classList.remove('is-loading');
      }
    }

    render(markup) {
      this.results.innerHTML = markup;
      this.options = Array.from(this.results.querySelectorAll('[data-search-option]'));
      this.activeIndex = -1;
      this.input.removeAttribute('aria-activedescendant');
      const msg = this.results.querySelector('[data-predictive-status]');
      this.announce(msg ? msg.textContent.trim() : '');
      this.showResults();
      if (window.Theme && Theme.initReveal) Theme.initReveal(this.results);
    }

    showResults() {
      this.results.hidden = false;
      if (this.defaults) this.defaults.hidden = true;
      this.input.setAttribute('aria-expanded', 'true');
      this.classList.add('has-results');
    }

    hideResults() {
      this.results.hidden = true;
      if (this.defaults) this.defaults.hidden = false;
      this.input.setAttribute('aria-expanded', 'false');
      this.input.removeAttribute('aria-activedescendant');
      this.activeIndex = -1;
      this.classList.remove('has-results');
    }

    announce(text) {
      if (this.status) this.status.textContent = text;
    }

    onKeydown(e) {
      const open = !this.results.hidden && this.options && this.options.length;
      switch (e.key) {
        case 'ArrowDown':
          if (!open) return;
          e.preventDefault();
          this.move(1);
          break;
        case 'ArrowUp':
          if (!open) return;
          e.preventDefault();
          this.move(-1);
          break;
        case 'Enter': {
          const active = open && this.options[this.activeIndex];
          if (active) {
            e.preventDefault();
            active.click();
          }
          break;
        }
        case 'Escape':
          // First Escape clears the field; a second one lets the drawer close
          if (this.input.value) {
            e.preventDefault();
            e.stopPropagation();
            this.input.value = '';
            this.toggleClear();
            this.hideResults();
          }
          break;
        default:
      }
    }

    move(step) {
      const count = this.options.length;
      // Slot -1 is the input itself, so the cycle has count + 1 positions
      this.activeIndex = ((this.activeIndex + 1 + step + count + 1) % (count + 1)) - 1;
      this.options.forEach((opt, i) => opt.setAttribute('aria-selected', String(i === this.activeIndex)));
      const active = this.options[this.activeIndex];
      if (active) {
        this.input.setAttribute('aria-activedescendant', active.id);
        active.scrollIntoView({ block: 'nearest' });
      } else {
        this.input.removeAttribute('aria-activedescendant');
      }
    }
  }

  customElements.define('predictive-search-form', PredictiveSearchForm);
})();
